"use client";

import LinkLoadingIndicator from "@/components/loader/link-loading-indicator";
import Link, { type LinkProps } from "next/link";
import { usePathname, useRouter } from "next/navigation";
import * as React from "react";
import { useTransition } from "react";

import { SEARCH_ENGINE_TAB_LINKS } from "@/app/(home)/_components/search-engine-tab";
import {
  getSearchTabFallbackHref,
  resolveSearchTabHref,
  type SearchTabId,
} from "@/app/(home)/_components/search-tab-navigation";
import { sidebarData } from "@/app/dashboard/(general)/_components/sidebar.data";
import { useAuth } from "@/components/providers/auth-provider";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { legalPageLinks } from "@/config/public-pages";
import { cn } from "@/lib/utils";
import type { INavItem } from "@/types/sidebar.types";
import { Home, LayoutDashboard } from "lucide-react";

type MobileNavLink = { href: string; label: string; external?: boolean };
type MobileSearchNavLink = MobileNavLink & { id: SearchTabId };

const mobileSupportLinks: MobileNavLink[] = [
  { href: "/blog", label: "Blog" },
  { href: "/contact-us", label: "Support" },
  { href: "/faqs", label: "FAQs" },
  { href: "/about-us", label: "About Us" },
];

const mobilePolicyLinks: MobileNavLink[] = legalPageLinks.map((page) => ({
  href: page.href,
  label: page.label,
}));

const mobileSearchLinks: MobileSearchNavLink[] = SEARCH_ENGINE_TAB_LINKS.map(
  (item) => ({
    id: item.id,
    href: item.href,
    label: item.label,
    external: item.external,
  }),
);

function isNavCollapsible(
  item: INavItem,
): item is Extract<INavItem, { items: unknown[] }> {
  return Array.isArray((item as { items?: unknown }).items);
}

export function MobileNav({
  className,
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  className?: string;
}) {
  const pathname = usePathname();
  const isDashboard = pathname.startsWith("/dashboard");
  const { isAuthenticated } = useAuth();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "extend-touch-target h-8 touch-manipulation items-center justify-start gap-2.5  hover:bg-transparent focus-visible:bg-transparent focus-visible:ring-0 active:bg-transparent dark:hover:bg-transparent",
            className,
          )}
        >
          <div className="relative flex h-8 w-4 items-center justify-center">
            <div className="relative size-4">
              <span
                className={cn(
                  "bg-foreground absolute left-0 block h-0.5 w-4 transition-all duration-100",
                  open ? "top-[0.4rem] -rotate-45" : "top-1",
                )}
              />
              <span
                className={cn(
                  "bg-foreground absolute left-0 block h-0.5 w-4 transition-all duration-100",
                  open ? "top-[0.4rem] rotate-45" : "top-2.5",
                )}
              />
            </div>
            <span className="sr-only">Toggle Menu</span>
          </div>
          <span className="flex h-8 items-center text-lg leading-none font-medium">
            Menu
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="bg-background/90 no-scrollbar h-(--radix-popper-available-height) w-(--radix-popper-available-width) overflow-y-auto rounded-none border-none p-0 shadow-none backdrop-blur duration-100 data-open:animate-none! -mt-1"
        align="start"
        side="bottom"
        alignOffset={-16}
        sideOffset={14}
      >
        <div className="flex flex-col gap-12 overflow-auto px-6 py-6 no-scrollbar">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3">
              {isDashboard ? (
                <>
                  <MobileLink href="/" onOpenChange={setOpen} icon={Home}>
                    Home
                  </MobileLink>

                  {sidebarData.navGroups.map((group) => (
                    <NavSection key={group.title} title={group.title}>
                      {group.items.map((item) => {
                        if (isNavCollapsible(item)) {
                          return (
                            <div
                              key={`${group.title}-${item.title}`}
                              className="flex flex-col gap-3"
                            >
                              {item.items.map((subItem) => (
                                <MobileLink
                                  key={`${group.title}-${item.title}-${subItem.title}`}
                                  href={subItem.url}
                                  onOpenChange={setOpen}
                                  className="pl-6"
                                  icon={subItem.icon}
                                >
                                  {subItem.title} Booking
                                </MobileLink>
                              ))}
                            </div>
                          );
                        }

                        return (
                          <MobileLink
                            key={`${group.title}-${item.title}`}
                            href={item.url}
                            onOpenChange={setOpen}
                            icon={item.icon}
                          >
                            {item.title}
                          </MobileLink>
                        );
                      })}
                    </NavSection>
                  ))}
                </>
              ) : (
                <>
                  {isAuthenticated && (
                    <MobileLink
                      href="/dashboard"
                      onOpenChange={setOpen}
                      icon={LayoutDashboard}
                    >
                      Dashboard
                    </MobileLink>
                  )}

                  <NavSection title="Search">
                    {mobileSearchLinks.map((item) => (
                      <SearchTabMobileLink
                        key={`search-${item.label}`}
                        tabId={item.id}
                        external={item.external}
                        onOpenChange={setOpen}
                      >
                        {item.label}
                      </SearchTabMobileLink>
                    ))}
                  </NavSection>

                  <NavSection title="Support">
                    {mobileSupportLinks.map((item) => (
                      <MobileLink
                        key={`support-${item.href}`}
                        href={item.href}
                        onOpenChange={setOpen}
                      >
                        {item.label}
                      </MobileLink>
                    ))}
                  </NavSection>

                  <NavSection title="Policies">
                    {mobilePolicyLinks.map((item) => (
                      <MobileLink
                        key={`policy-${item.href}`}
                        href={item.href}
                        onOpenChange={setOpen}
                      >
                        {item.label}
                      </MobileLink>
                    ))}
                  </NavSection>
                </>
              )}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function NavSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </p>
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  );
}

function SearchTabMobileLink({
  tabId,
  external,
  onOpenChange,
  children,
}: {
  tabId: SearchTabId;
  external?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const href = getSearchTabFallbackHref(tabId);

  if (external) {
    return (
      <MobileLink href={href} external={external} onOpenChange={onOpenChange}>
        {children}
      </MobileLink>
    );
  }

  return (
    <Link
      href={href}
      onClick={(event) => {
        event.preventDefault();
        startTransition(() => {
          onOpenChange?.(false);
          router.push(resolveSearchTabHref(tabId));
        });
      }}
      aria-disabled={isPending}
      className={cn(
        "flex items-center gap-2 text-2xl font-medium transition-opacity",
        isPending && "pointer-events-none opacity-60",
      )}
    >
      <LinkLoadingIndicator
        title={typeof children === "string" ? children : undefined}
        element={typeof children !== "string" ? children : undefined}
      />
    </Link>
  );
}

function MobileLink({
  href,
  external,
  onOpenChange,
  className,
  icon: Icon,
  children,
  ...props
}: LinkProps & {
  external?: boolean;
  onOpenChange?: (open: boolean) => void;
  icon?: React.ElementType;
  children: React.ReactNode;
  className?: string;
}) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  if (external) {
    return (
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => onOpenChange?.(false)}
        className={cn(
          "flex items-center gap-2 text-2xl font-medium",
          className,
        )}
        {...props}
      >
        {Icon ? <Icon className="size-4" /> : null}
        {children}
      </Link>
    );
  }

  return (
    <Link
      href={href}
      onClick={(e) => {
        e.preventDefault();
        startTransition(() => {
          onOpenChange?.(false);
          router.push(typeof href === "string" ? href : String(href));
        });
      }}
      aria-disabled={isPending}
      className={cn(
        "flex items-center gap-2 text-2xl font-medium transition-opacity",
        isPending && "pointer-events-none opacity-60",
        className,
      )}
      {...props}
    >
      {Icon ? (
        <Icon className={cn("size-4", isPending && "animate-pulse")} />
      ) : null}
      <LinkLoadingIndicator
        title={typeof children === "string" ? children : undefined}
        element={typeof children !== "string" ? children : undefined}
      />
    </Link>
  );
}
